<script lang="ts">
  import { debounce } from 'lodash-es'
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import { onChange } from '#app/lib/svelte/svelte'
  import Spinner from '#components/spinner.svelte'
  import GroupProfile from '#groups/components/group_profile.svelte'
  import type { SerializedGroup } from '#groups/lib/groups'
  import { getNearbyItems } from '#inventory/lib/queries'
  import type { FocusedSection } from '#inventory/types/inventory_browser'
  import GroupMarker from '#map/components/group_marker.svelte'
  import LeafletMap from '#map/components/leaflet_map.svelte'
  import Marker from '#map/components/marker.svelte'
  import PositionRequired from '#map/components/position_required.svelte'
  import UserMarker from '#map/components/user_marker.svelte'
  import ZoomInToDisplayMore from '#map/components/zoom_in_to_display_more.svelte'
  import { getBbox, isMapTooZoomedOut } from '#map/lib/map'
  import type { BBox, LatLng } from '#server/types/common'
  import type { GroupId } from '#server/types/group'
  import type { UserId } from '#server/types/user'
  import { i18n, I18n } from '#user/lib/i18n'
  import { mainUserStore } from '#user/lib/serialized_main_user'
  import { getGroupsByPosition, getUsersByPosition } from '#users/components/lib/public_users_nav_helpers'
  import PaginatedSectionItems from '#users/components/paginated_section_items.svelte'
  import UserProfile from '#users/components/user_profile.svelte'
  import UsersHomeSectionList from '#users/components/users_home_section_list.svelte'
  import type { SerializedUser } from '#users/lib/users'
  import type { Map } from 'leaflet'

  let {
    filter,
    focusedSection = $bindable(),
  }: {
    filter?: 'users' | 'groups'
    focusedSection: FocusedSection
  } = $props()

  const showUsers = $derived(filter !== 'groups')
  const showGroups = $derived(filter !== 'users')

  let usersByIds: Record<UserId, SerializedUser> = $state({})
  let groupsByIds: Record<GroupId, SerializedGroup> = $state({})
  let map: Map = $state()
  let bbox: BBox
  let mapViewLatLng: LatLng = $state()
  let mapZoom: number = $state()
  let flash: FlashState = $state()
  let zoomInToDisplayMore: boolean = $state()
  let displayedElementsCount: number = $state()

  async function getByPosition (name: string, bbox: BBox) {
    try {
      if (name === 'users') {
        usersByIds = await getUsersByPosition({ bbox })
        usersInBounds = Object.values(usersByIds)
      } else if (name === 'groups') {
        groupsByIds = await getGroupsByPosition({ bbox })
        groupsInBounds = Object.values(groupsByIds)
      }
    } catch (err) {
      flash = err
    }
  }

  let waitForUsers: Promise<void> = $state()
  let waitForGroups = $state()
  function fetchAndShowUsersAndGroupsOnMap () {
    if (!map) return

    displayedElementsCount = getDisplayedElementsCount()
    if (isMapTooZoomedOut(mapZoom, displayedElementsCount)) return

    bbox = getBbox(map)
    if (!bbox) return

    if (showUsers) {
      waitForUsers = getByPosition('users', bbox)
    }

    if (showGroups) {
      waitForGroups = getByPosition('groups', bbox)
    }
  }
  const lazyFetchAndShowUsersAndGroupsOnMap = debounce(fetchAndShowUsersAndGroupsOnMap, 200)

  async function onMainUserPositionChange () {
    const positionChanged = mapViewLatLng != null
    mapViewLatLng = $mainUserStore.position
    if (positionChanged) lazyFetchAndShowUsersAndGroupsOnMap()
  }

  function getDisplayedElementsCount () {
    if (!usersInBounds || !groupsInBounds) return 0
    return usersInBounds.length + groupsInBounds.length
  }

  let usersInBounds: SerializedUser[] = $state()
  let groupsInBounds: SerializedGroup[] = $state()

  $effect(() => {
    onChange(map, lazyFetchAndShowUsersAndGroupsOnMap)
  })
  $effect(() => {
    onChange($mainUserStore.position, onMainUserPositionChange)
  })

  let selectedUser: SerializedUser = $state()
  let selectedGroup: SerializedGroup = $state()
  function onSelectUser (user: SerializedUser) {
    selectedUser = user
    selectedGroup = null
    focusedSection = { type: 'user' }
  }

  function onSelectGroup (group: SerializedGroup) {
    selectedUser = null
    selectedGroup = group
    focusedSection = { type: 'group' }
  }

  function unselect () {
    selectedUser = null
    selectedGroup = null
    focusedSection = { type: null }
  }
</script>

{#if $mainUserStore.position != null}
  <div class="map-lists-wrapper">
    <div class="lists">
      {#if showUsers}
        <div class="list-wrapper">
          <div class="list-header">
            <h2 class="list-label">{I18n('users')}</h2>
            {#await waitForUsers}
              <div class="usersLoading"><Spinner /></div>
            {/await}
          </div>
          {#if usersInBounds}
            <UsersHomeSectionList
              docs={usersInBounds}
              type="users"
              hideList={zoomInToDisplayMore}
              hideListMessage={i18n('Zoom-in to display more')}
              onSelectDoc={({ doc }) => onSelectUser(doc as SerializedUser)}
            />
          {/if}
        </div>
      {/if}

      {#if showGroups}
        <div class="list-wrapper">
          <div class="list-header">
            <h2 class="list-label">{I18n('groups')}</h2>
            {#await waitForGroups}
              <div class="groupsLoading"><Spinner /></div>
            {/await}
          </div>
          {#if groupsInBounds}
            <UsersHomeSectionList
              docs={groupsInBounds}
              type="groups"
              hideList={zoomInToDisplayMore}
              hideListMessage={i18n('Zoom-in to display more')}
              onSelectDoc={({ doc }) => onSelectGroup(doc as SerializedGroup)}
            />
          {/if}
        </div>
      {/if}
    </div>

    <div id="mapContainer">
      {#if mapViewLatLng}
        <LeafletMap
          bind:map
          view={mapViewLatLng}
          bind:zoom={mapZoom}
          cluster={true}
          showLocationSearchInput={true}
          on:moveend={lazyFetchAndShowUsersAndGroupsOnMap}
        >
          <Marker latLng={$mainUserStore.position} standalone={true}>
            <UserMarker doc={$mainUserStore} {onSelectUser} />
          </Marker>
          {#if usersInBounds && !zoomInToDisplayMore}
            {#each usersInBounds as user (user._id)}
              {#if !user.isMainUser}
                <Marker latLng={user.position} standalone={user.isMainUser}>
                  <UserMarker doc={user} {onSelectUser} />
                </Marker>
              {/if}
            {/each}
          {/if}

          {#if groupsInBounds && !zoomInToDisplayMore}
            {#each groupsInBounds as group (group._id)}
              <Marker latLng={group.position}>
                <GroupMarker doc={group} {onSelectGroup} />
              </Marker>
            {/each}
          {/if}
          <ZoomInToDisplayMore
            {mapZoom}
            {displayedElementsCount}
            bind:zoomInToDisplayMore
          />

        </LeafletMap>
      {/if}
    </div>
  </div>

  {#if selectedUser}
    <!-- Recreate component when selectedUser changes, see https://svelte.dev/docs#template-syntax-key -->
    {#key selectedUser}
      <UserProfile user={selectedUser} {focusedSection} onUnselectProfile={unselect} />
    {/key}
  {:else if selectedGroup}
    {#key selectedGroup}
      <GroupProfile group={selectedGroup} {focusedSection} unselectGroup={unselect} />
    {/key}
  {:else}
    <!-- TODO: use bbox to update displayed items accordingly -->
    <PaginatedSectionItems
      sectionRequest={getNearbyItems}
      showDistance={true}
    />
  {/if}
{:else}
  <PositionRequired />
{/if}

<Flash state={flash} />

<style lang="scss">
  @import "general/scss/utils";
  @import "users/scss/users_home_section_navs";

  .list-header{
    @include display-flex(row, center, space-between);
  }

  .usersLoading, .groupsLoading{
    margin-inline-start: 1em;
    opacity: 0.6;
  }

  .map-lists-wrapper{
    display: flex;
  }

  #mapContainer{
    z-index: 0;
    @include display-flex(row, center, center);
    background-color: $off-white;
    position: relative;
    :global(.items-count), :global(.group-admin-badge), :global(.members-count){
      position: absolute;
      background-color: white;
      color: $dark-grey;
      line-height: 0;
      min-width: 1em;
    }
    :global(.items-count), :global(.members-count){
      inset-block-start: 0;
      inset-inline-end: 0;
      text-align: center;
      padding: 0.2em 0;
      border-end-start-radius: $global-radius;
      @include transition;
    }
    :global(.group-admin-badge){
      inset-block-start: 0;
      inset-inline-start: 0;
      // Somehow centers the icon vertically
      line-height: 0;
      border-end-end-radius: $global-radius;
    }
  }

  /* Small screens */
  @media screen and (width < $small-screen){
    .lists{
      flex-direction: column;
    }
    .map-lists-wrapper{
      flex-direction: column;
    }
    #mapContainer{
      order: -1;
      height: 20em;
      max-height: 50vh;
    }
  }

  /* Large screens */
  @media screen and (width >= $small-screen){
    .map-lists-wrapper{
      > div{
        flex: 1 0 0;
      }
    }
    #mapContainer{
      margin-inline-start: 0.6em;
      height: $map-large-screen-heigth;
    }
  }
</style>
